import { useCallback } from 'react'

import { useAccount, useChainId } from 'wagmi'

import { AirdropList, GasParams, Params as TxParams, Strategies, Token } from 'types/entities'

import { useAirdropper } from 'hooks/airdrop'
import { useCreateBatchRequest } from 'hooks/wallet'

import { useTxsList } from 'state/airdrop/hooks'
import { useUpdateTransactionHistory } from 'state/user/hooks'

import { errors } from 'constants/errors'
import { TxStatuses, TxTypes } from 'constants/transaction'

type Params = {
  token: Token
  batchSize: bigint
  airdropId: string
  strategy: Strategies
  isDeflationary: boolean
  airdropList: AirdropList
  airdropChainId: SupportedChainId
}

export function useAirdrop() {
  const chainId = useChainId()
  const { address: customer } = useAccount()

  const txsData = useTxsList()
  const airdropper = useAirdropper()
  const onUpdateTx = useUpdateTransactionHistory()
  const createBatchRequest = useCreateBatchRequest()

  return useCallback(
    async (params: Params, gasParams?: GasParams) => {
      try {
        if (!customer) {
          throw new Error(errors.wallet.WALLET_REQUIRED)
        }

        // ToDo write formatter
        const _txsData = txsData.map(
          (txData) =>
            ({
              to: txData.to,
              from: txData.from,
              data: txData.data,
              nonce: txData.nonce,
              gas: txData.gas ? BigInt(txData.gas) : undefined,
              value: txData.value ? BigInt(txData.value) : undefined,
              ...(gasParams || {}),
            } as TxParams),
        )

        const txs = await createBatchRequest(_txsData, (txHash, index) => {
          onUpdateTx({
            txHash,
            chainId,
            from: customer,
            token: params.token,
            to: txsData[index].to,
            type: TxTypes.AIRDROP,
            airdropId: params.airdropId,
            status: TxStatuses.PENDING,
            value: txsData[index].value.toString(),
          })
        })

        return txs
      } catch (err) {
        const [errors]: string[] = Array(err.message.toString())
        const [text] = errors.split(`\n`)

        throw new Error(text)
      }
    },
    [chainId, customer, airdropper, createBatchRequest, onUpdateTx],
  )
}
